import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { Box, Skeleton, Tooltip, Typography } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';

import { TranslationKeys } from 'app/translations';

import { getDisplayCurrencyValue, getDisplayDateValue, getDisplayUTCDateValue } from 'app/utils';
import format from 'app/utils/formatter';

import type { EmployeeEmployment, EmployeeEmploymentWage } from 'app/types/employee.types';
import type { UseQueryRefetch } from 'app/types/useQuery.types';

import ActionButton from 'app/components/ActionButton';
import DetailsPanel from 'app/components/DetailsPanel';
import DetailsPanelRow from 'app/components/DetailsPanelRow';
import { useLocale } from 'app/components/Intl';

import { EmployeeDetailsPanelHeaderWithSyncToggle } from 'app/Domain/Employees/Pages/EmployeeDetailPage/components/EmployeeDetailsPanelHeaderWithSyncToggle';
import { DivPanelBodyDetailsStyled } from 'app/Domain/Employees/Pages/EmployeeDetailPage/components/EmployeeEmploymentTab/EmployeeEmploymentTab.styles';

import type { Employer } from 'app/types';
import { EmployeeEmploymentWageFormDialog } from './components';
import { EmployeeEmploymentWageFields } from './EmployeeEmploymentWages.types';

import { CONNECTION, EMPLOYEE } from 'app/common/Authorization/entities';
import { DELETE, EDIT, RUN } from 'app/common/Authorization/permissions';
import { ConfirmDialog, HasAccessTo } from 'app/components';
import { useDialog, useSnakeBar } from 'app/hooks';
import { useEmployeeDeleteWageMutation } from 'app/hooks/api/employee/useEmployeeDeleteWageMutation';

type EmployeeEmploymentWagesProps = {
  loading: boolean;
  isRegularConnection: boolean;
  refetchGetEmploymentsQuery: UseQueryRefetch;
  connectionId: string | undefined;
  employeeId: string | undefined;
  employment: EmployeeEmployment | null;
  synchronizationStatus: boolean | undefined;
  refetchGetEmployeeSynchronizationQuery: UseQueryRefetch;
  disabledActionButtonsWhileLoading: boolean;
  employerId: Employer['employerId'] | undefined;
  employeeStartDate: string | undefined;
};

export const EmployeeEmploymentWages = ({
  connectionId,
  employeeId,
  employment,
  refetchGetEmploymentsQuery,
  loading,
  isRegularConnection,
  synchronizationStatus,
  refetchGetEmployeeSynchronizationQuery,
  disabledActionButtonsWhileLoading,
  employerId,
  employeeStartDate,
}: EmployeeEmploymentWagesProps) => {
  const { locale } = useLocale();
  const intl = useIntl();
  const { showSuccessSnakeBar, showErrorSnakeBar } = useSnakeBar();

  const {
    dialogState: wageFormDialogState,
    openDialog: openWageFormDialog,
    closeDialog: closeWageFormDialog,
  } = useDialog();

  const {
    dialogState: deleteWageDialog,
    openDialog: openConfirmDeleteDialog,
    closeDialog: closeConfirmDeleteDialog,
  } = useDialog();

  const { mutate: deleteEmployeeWage } = useEmployeeDeleteWageMutation();

  const [expanded, setExpanded] = React.useState((employment?.wages ?? []).map(() => false));
  const [wageToEdit, setWageToEdit] = React.useState<EmployeeEmploymentWage | null>(null);
  const [wageToDelete, setWageToDelete] = React.useState<EmployeeEmploymentWage | null>(null);
  const hasWages = employment && employment?.wages?.length > 0;
  const hasActiveSynchronization = synchronizationStatus !== undefined ? synchronizationStatus : false;
  const disabledIfNoAccessToEmployeeEdit = !HasAccessTo(EMPLOYEE, EDIT);
  const disabledIfNoAccessToConnectionDelete = !HasAccessTo(CONNECTION, DELETE);
  const disabledIfNoAccessToConnectionRun = !HasAccessTo(CONNECTION, RUN);

  const shouldRenderEmployeeEmploymentWageFormDialog =
    !!employerId && !!connectionId && !!employeeId && !!employment && wageFormDialogState;

  const toggleWageDetailsPanel = React.useCallback(
    ({ id, expanded: newExpanded }) => {
      const newState = new Array(expanded.length).fill(false);
      newState[id] = !newExpanded;
      setExpanded(newState);
    },
    [expanded],
  );

  const onOpenWageFormDialog = React.useCallback(
    (wage?: EmployeeEmploymentWage) => {
      setWageToEdit(wage || null);
      openWageFormDialog();
    },
    [openWageFormDialog],
  );

  const onCloseWageFormDialog = React.useCallback(() => {
    setWageToEdit(null);
    closeWageFormDialog();
  }, [closeWageFormDialog]);

  const onClickAddButton = React.useCallback(() => {
    onOpenWageFormDialog();
  }, [onOpenWageFormDialog]);

  const onClickEditButton = React.useCallback(
    (wage: EmployeeEmploymentWage) => () => {
      onOpenWageFormDialog(wage);
    },
    [onOpenWageFormDialog],
  );

  const onClickDeleteButton = React.useCallback(
    (wage: EmployeeEmploymentWage) => () => {
      setWageToDelete(wage);
      openConfirmDeleteDialog();
    },
    [openConfirmDeleteDialog],
  );

  const onCloseDeleteWage = React.useCallback(() => {
    setWageToDelete(null);
    closeConfirmDeleteDialog();
  }, [closeConfirmDeleteDialog]);

  const onConfirmDeleteWage = React.useCallback(() => {
    if (!connectionId || !employeeId || !wageToDelete) {
      return;
    }
    const method = 'deleteEmployeeWage';
    deleteEmployeeWage(
      {
        connectionId: connectionId,
        employeeId: employeeId,
        wageId: wageToDelete.wageId,
      },
      {
        onSuccess: () => {
          refetchGetEmploymentsQuery();
          showSuccessSnakeBar({
            method,
            message: intl.formatMessage({ id: TranslationKeys.global_update_successMessage }),
          });
          onCloseDeleteWage();
        },
        onError: () => {
          showErrorSnakeBar({
            method,
            message: intl.formatMessage({ id: TranslationKeys.global_update_errorMessage }),
          });
        },
      },
    );
  }, [
    connectionId,
    employeeId,
    wageToDelete,
    refetchGetEmploymentsQuery,
    showSuccessSnakeBar,
    showErrorSnakeBar,
    deleteEmployeeWage,
    onCloseDeleteWage,
    intl,
  ]);

  const LoadingDetailsBody = React.useMemo(() => {
    return (
      <>
        {new Array(5).fill(null).map((_, idx) => (
          <Skeleton key={idx} height={24} />
        ))}
      </>
    );
  }, []);

  const NoWagesDetailsBody = React.useMemo(() => {
    return (
      <Box sx={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Typography variant="body2">
          <FormattedMessage id={TranslationKeys.employees_detail_wages_noWages} />
        </Typography>
      </Box>
    );
  }, []);

  const WagesDetailsBody = React.useMemo(() => {
    return (employment?.wages || []).map((wage, idx) => {
      const expandedIfOnlyOneWage = employment?.wages.length === 1;
      const isExpanded = typeof expanded[idx] !== 'undefined' ? expanded[idx] : false;

      const rows: Record<string, any> = {
        [EmployeeEmploymentWageFields.StartDate.Label]: getDisplayDateValue(wage?.startDate),
        [EmployeeEmploymentWageFields.EndDate.Label]: getDisplayUTCDateValue(wage?.endDate),
        [EmployeeEmploymentWageFields.GrossWage.Label]: getDisplayCurrencyValue({
          locale,
          value: wage?.grossWage,
          options: EmployeeEmploymentWageFields.GrossWage.Options,
        }),
        [EmployeeEmploymentWageFields.HourlyWage.Label]: getDisplayCurrencyValue({
          locale,
          value: wage?.hourlyWage,
          options: EmployeeEmploymentWageFields.HourlyWage.Options,
        }),
        [EmployeeEmploymentWageFields.NetWage.Label]: getDisplayCurrencyValue({
          locale,
          value: wage?.netWage,
          options: EmployeeEmploymentWageFields.NetWage.Options,
        }),
        [EmployeeEmploymentWageFields.SvWage.Label]: getDisplayCurrencyValue({
          locale,
          value: wage?.svWage,
          options: EmployeeEmploymentWageFields.SvWage.Options,
        }),
      };

      if (wage?.salaryComponents && wage?.salaryComponents?.length > 0) {
        const SalaryComponentsDropdown = () => {
          const [componentExpanded, setComponentExpanded] = React.useState(false);
          const totalValue = getDisplayCurrencyValue({
            locale,
            value: wage?.salaryComponentsTotal,
            options: EmployeeEmploymentWageFields.SalaryComponentsTotal.Options,
          });
          const componentRows: Record<string, any> = {};
          wage.salaryComponents?.forEach((component, componentIdx) => {
            componentRows[`${component.code}`] = getDisplayCurrencyValue({
              locale,
              value: component.value,
              options: EmployeeEmploymentWageFields.SalaryComponentsTotal.Options,
            });
          });
          return (
            <DetailsPanelRow
              title={<Typography variant="h6">{`${totalValue}`}</Typography>}
              expanded={componentExpanded}
              rows={componentRows}
              loading={false}
              id={idx}
              onExpand={({ expanded: newExpanded }) => setComponentExpanded(!newExpanded)}
            />
          );
        };
        rows[EmployeeEmploymentWageFields.SalaryComponentsTotal.Label] = <SalaryComponentsDropdown />;
      }
      return (
        <div key={idx}>
          <DetailsPanelRow
            title={
              wage.deletedBySource || wage.isSalaryTable ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6">{format.date(wage.startDate)}</Typography>
                  {wage.deletedBySource && (
                    <Tooltip title={<FormattedMessage id="employees.detail.employment.deletedBySource" />}>
                      <InfoIcon fontSize="small" sx={{ color: 'warning.main' }} />
                    </Tooltip>
                  )}
                  {wage.isSalaryTable && (
                    <Tooltip title={<FormattedMessage id="employees.detail.employment.wage.isSalaryTable" />}>
                      <InfoIcon fontSize="small" sx={{ color: 'warning.main' }} />
                    </Tooltip>
                  )}
                </Box>
              ) : (
                <Typography variant="h6">{format.date(wage.startDate)}</Typography>
              )
            }
            expanded={expandedIfOnlyOneWage || isExpanded}
            rows={rows}
            loading={loading}
            id={idx}
            onExpand={toggleWageDetailsPanel}
            onDeleteButtonClick={isRegularConnection ? onClickDeleteButton(wage) : undefined}
            onEditButtonClick={isRegularConnection ? onClickEditButton(wage) : undefined}
            disabledEditButton={
              disabledActionButtonsWhileLoading ||
              hasActiveSynchronization ||
              disabledIfNoAccessToEmployeeEdit ||
              disabledIfNoAccessToConnectionRun
            }
            disabledDeleteButton={
              disabledActionButtonsWhileLoading ||
              hasActiveSynchronization ||
              disabledIfNoAccessToConnectionDelete ||
              disabledIfNoAccessToConnectionRun
            }
          />
        </div>
      );
    });
  }, [
    employment?.wages,
    expanded,
    locale,
    loading,
    toggleWageDetailsPanel,
    isRegularConnection,
    onClickEditButton,
    onClickDeleteButton,
    disabledActionButtonsWhileLoading,
    hasActiveSynchronization,
    disabledIfNoAccessToEmployeeEdit,
    disabledIfNoAccessToConnectionRun,
    disabledIfNoAccessToConnectionDelete,
  ]);

  const DetailsPanelBody = React.useMemo(() => {
    const finishedLoadingAndHasWages = !loading && hasWages;
    const finishedLoadingAndHasNoWages = !loading && !hasWages;

    return (
      <DivPanelBodyDetailsStyled isRegularConnection={isRegularConnection}>
        <>
          {loading && LoadingDetailsBody}
          {finishedLoadingAndHasWages && WagesDetailsBody}
          {finishedLoadingAndHasNoWages && NoWagesDetailsBody}
        </>
      </DivPanelBodyDetailsStyled>
    );
  }, [loading, hasWages, isRegularConnection, LoadingDetailsBody, WagesDetailsBody, NoWagesDetailsBody]);

  const DetailsPanelActions = React.useMemo(() => {
    return (
      <ActionButton
        messageId={TranslationKeys.button_employmentWages_add}
        variant="text"
        icon={faPlus}
        onClick={onClickAddButton}
        disabled={
          disabledActionButtonsWhileLoading ||
          hasActiveSynchronization ||
          disabledIfNoAccessToEmployeeEdit ||
          disabledIfNoAccessToConnectionRun
        }
      />
    );
  }, [
    disabledActionButtonsWhileLoading,
    disabledIfNoAccessToEmployeeEdit,
    hasActiveSynchronization,
    onClickAddButton,
    disabledIfNoAccessToConnectionRun,
  ]);

  const DetailsPanelHeader = React.useMemo(() => {
    return (
      <EmployeeDetailsPanelHeaderWithSyncToggle
        field={'wage'}
        loading={loading}
        synchronizationStatus={synchronizationStatus}
        refetchGetEmployeeSynchronizationQuery={refetchGetEmployeeSynchronizationQuery}
        disabledActionButtonsWhileLoading={disabledActionButtonsWhileLoading}
        messageId={TranslationKeys.employees_detail_wages}
        employeeId={employeeId}
        connectionId={connectionId}
      />
    );
  }, [
    loading,
    synchronizationStatus,
    refetchGetEmployeeSynchronizationQuery,
    disabledActionButtonsWhileLoading,
    employeeId,
    connectionId,
  ]);

  return (
    <>
      {shouldRenderEmployeeEmploymentWageFormDialog && (
        <EmployeeEmploymentWageFormDialog
          open={wageFormDialogState}
          closeDialog={onCloseWageFormDialog}
          employerId={employerId}
          connectionId={connectionId}
          employeeId={employeeId}
          employeeStartDate={employeeStartDate}
          employmentId={employment?.employmentId}
          wage={wageToEdit}
          refetchGetEmploymentsQuery={refetchGetEmploymentsQuery}
        />
      )}

      {deleteWageDialog && (
        <ConfirmDialog
          title={<FormattedMessage id={TranslationKeys.employees_detail_wages_confirmDeleteTitle} />}
          open={deleteWageDialog}
          onConfirm={onConfirmDeleteWage}
          onClose={onCloseDeleteWage}
        >
          <FormattedMessage id={TranslationKeys.employees_detail_wages_confirmDeleteMessage} />
        </ConfirmDialog>
      )}

      <DetailsPanel
        header={DetailsPanelHeader}
        body={DetailsPanelBody}
        actions={isRegularConnection ? DetailsPanelActions : undefined}
        scrollBody={true}
      />
    </>
  );
};
